import React, { FC, memo } from 'react';
import clsx from 'clsx';
import { useToggleRefinement } from 'react-instantsearch';
import { TranslationKey } from '~/lib';

import { useTranslation } from '~/shared/utils/translation';
import { IRefinementListToggles, RefinementToggleAttribute } from './RefinementList.definitions';
import { Button } from '~/shared/components';
import Flex from '~/shared/components/Flex';
import styles from '~/features/productList/components/RefinementListRow/RefinementListRow.module.scss';
import Checkmark from '~/icons/checkmark.svg';
import { useDrawerFilterTracking } from '~/features/productList/hooks/useDrawerFilterTracking';

interface RefinementListToggleItem {
    attribute: RefinementToggleAttribute;
}

const RefinementListToggleItem: FC<RefinementListToggleItem> = ({ attribute }) => {
    const { translate } = useTranslation();

    const { trackFilter } = useDrawerFilterTracking();
    const { refine, value } = useToggleRefinement(attribute);
    const translationKey = (attribute.legend ||
        `plp.facet.${attribute.attribute}`) as TranslationKey;
    const legend = translate(translationKey);

    const handleRefine = () => {
        if (refine) {
            refine(value);
            trackFilter(attribute.attribute, legend, { isRefined: value.isRefined });
        }
    };

    return (
        <Flex grow className={styles.dropdownItemContainer}>
            <Button
                className={styles.dropdownItemButton}
                fullWidth
                variant="plain"
                onClick={handleRefine}
                tabIndex={-1}
            >
                <Flex grow alignItems="center" className={styles.dropdownItemText}>
                    <Flex className={styles.dropdownTextWrapper}>
                        <span
                            className={clsx(styles.checkmarkContainer, {
                                [styles.isRefined]: value.isRefined,
                            })}
                            role="checkbox"
                            aria-label={`${legend}, ${value.count} produkter`}
                            aria-checked={value.isRefined}
                            tabIndex={0}
                            onKeyDown={(event) => {
                                if (event.key !== ' ' && event.key !== 'Enter') return;
                                event.preventDefault();
                                handleRefine();
                            }}
                        >
                            {value.isRefined && <Checkmark className={styles.checkmark} />}
                        </span>
                        {legend}
                    </Flex>

                    <span className={styles.dropdownValueWrapper}>
                        {value.count ? value.count : ''}
                    </span>
                </Flex>
            </Button>
        </Flex>
    );
};

const RefinementListToggle: FC<IRefinementListToggles> = ({ attributes }) => (
    <Flex column>
        {attributes.map((attribute, index) => (
            <RefinementListToggleItem
                key={`${attribute.attribute}}-${index}`}
                attribute={attribute}
            />
        ))}
    </Flex>
);

export default memo(RefinementListToggle);
